import { EventValues } from '@sial/common-utils'
import { extractData } from '@utils/errorHandler'
import { ApolloError } from 'apollo-boost'
import {
  sendEvent,
  regexForStripBrackets,
  regexForStripHTML,
  regexForStripSymbols,
  regexForStripEncodingUnicode,
  regexForStripDashes,
} from '@utils/analytics/coreAnalytics'
import {
  DiscontinuedMaterialPricingDetailFragment,
  ValidMaterialPricingDetailFragment,
} from '@src/fragments/ProductPricing.generated'
import {
  AddToCartPagesEnum,
  SharedListEventAction,
} from '@utils/analytics/enums'
import { ProductCardType } from '../searchUtils'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { sendErrorEvent } from '@utils/analytics'

//Product pricing and availability has it's own smaller messages file located near the bulk of its components.
import messages from '@src/components/ProductPriceAvailability/messages'
import { ExcludedProductTypes } from '@src/aem-content/aem-types'

export const PricingAndAvailabilityPanelType: Record<
  Exclude<ProductCardType, ExcludedProductTypes>,
  string
> = {
  [ProductCardType.BuyItAgain]: 'buy it again',
  [ProductCardType.CustomersAlsoViewed]: 'customers also viewed',
  [ProductCardType.FrequentlyPurchased]: 'frequently purchased',
  [ProductCardType.Recently]: 'recently viewed products',
  [ProductCardType.Recommended]: 'recommended products',
  [ProductCardType.Featured]: 'featured products',
  [ProductCardType.Related]: 'related products',
  [ProductCardType.CartRecommendedProducts]: 'cart_recommended_products',
  [ProductCardType.CompareSimilarItems]: 'compare similar items',
  [ProductCardType.BestSellers]: '', // TODO: Best Sellers Analytics TBD
  [ProductCardType.ProductHeroCard]: 'product hero card',
  [ProductCardType.PopularProducts]: 'popular products',
  [ProductCardType.NewArrivals]: 'new arrivals',
  [ProductCardType.BuyAgainHomepage]: 'buy again',
  [ProductCardType.DiscountedProducts]: 'top deals',
  [ProductCardType.NewArrivalProducts]: 'new arrivals',
  [ProductCardType.RecentlyViewedProducts]: 'recently viewed products',
}

export enum PricingAndAvailabilityAction {
  HeaderSearchProductIdQuickView = 'product ID quick view - header search',
  HeaderSearchProductVariantQuickView = 'product variant quick view - header search',
}

export const getPricingAndAvailabilityEventAction = (
  listAction: SharedListEventAction,
  carouselType?: ProductCardType
): string => {
  return carouselType
    ? `${listAction} - ${PricingAndAvailabilityPanelType[carouselType]}`
    : listAction
}

interface PricingErrors {
  [productNumber: string]: DiscontinuedMaterialPricingDetailFragment
}

export const sendBeginRequestEvent = ({
  product,
  materialNumber,
  cta,
}: {
  product: Partial<PdpFieldsFragment>
  materialNumber: string | undefined
  cta:
    | {
        __typename?: 'Form' | undefined
        label: string
        type: string
        url: string
      }
    | undefined
}): void => {
  const event =
    cta?.type === 'more information'
      ? 'more_info'
      : cta?.type.split(' ').join('_')
  const basePayload = {
    event: `begin_${event}_request`,
    action: undefined,
    detail: undefined,
    section: 'pricing card',
    component: 'right rail',
    element_text: cta?.label.toLowerCase(),
    element_type: 'link',
    link_url: cta?.url,
    core_event: 'yes',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: product.productNumber?.toLowerCase(),
    product_brand: product.brand?.key.toLowerCase(),
    product_variant: materialNumber?.toLowerCase(),
  }
  sendEvent({ payload: basePayload })
}

export const sendPricingAvailabilityInteractionEvent = ({
  action,
  detail,
  section,
  component,
  elementType,
  elementText,
  material,
  gaType,
}: {
  action: string
  detail?: string
  section?: string
  component: string
  elementType?: string
  elementText?: string
  material?: Partial<ValidMaterialPricingDetailFragment>
  gaType?: AddToCartPagesEnum
}): void => {
  const basePayload = {
    event: 'pricing_availability_interaction',
    action,
    detail,
    gaType,
    section: section?.toLowerCase() || undefined,
    component,
    element_type: elementType,
    element_text: elementText,
    link_url: undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: material?.product?.toLowerCase(),
    product_variant: material?.materialNumber?.toLowerCase() || undefined,
    product_brand: material?.brand?.toLowerCase(),
    product_name: material?.materialDescription
      ?.replace(regexForStripHTML, '')
      .replace(regexForStripSymbols, '')
      .replace(regexForStripEncodingUnicode, '')
      .replace(regexForStripDashes, ' ')
      .toLowerCase(),
  }
  sendEvent({ payload: basePayload })
}

export const sendPricingAndAvailabilityErrorEvent = (
  productNumber: string,
  error: ApolloError | undefined,
  messageCode?: string,
  productBrand?: string,
  productName?: string
): void => {
  const errorObject = error ? extractData(error).errors[0] : undefined
  const errorMessage =
    errorObject?.code && messages[errorObject.code]
      ? messages[errorObject.code]
      : messageCode
        ? messages[messageCode]
        : messages.PRICING_AND_AVAILABILITY_UNAVAILABLE
  const basePayload = {
    event: 'exception',
    description:
      errorMessage?.defaultMessage
        .replace(regexForStripBrackets, ' ')
        .toLowerCase() || undefined,
    error_category: 'p&a errors',
    product_id: productNumber.toLowerCase(),
    product_name:
      productName
        ?.replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .replace(regexForStripEncodingUnicode, '')
        .toLowerCase() || undefined,
    product_brand: productBrand?.toLowerCase() || undefined,
  }

  sendEvent({ payload: basePayload })
}

export const sendQuickOrderPandAErrorEvent = (
  pricingErrors: PricingErrors
): void => {
  const productNumbers = Object.keys(pricingErrors) || []

  if (!productNumbers?.length) return

  const eventLabel = productNumbers.map((productNumber) => {
    const errorCode =
      pricingErrors[productNumber]?.errorMsg ===
      'PRICING_AND_AVAILABILITY_UNAVAILABLE'
        ? 'THIS_PRODUCT_NUMBER_DOES_NOT_EXIST'
        : pricingErrors[productNumber]?.errorMsg || EventValues.Empty

    const errorMessage = messages[errorCode]
      ? `${productNumber} - ${messages[errorCode].defaultMessage}`
      : 'invalid product number'
    return errorMessage.replace(regexForStripBrackets, ' ') || EventValues.Empty
  })

  sendErrorEvent({
    description: eventLabel?.join(' | ') || undefined,
    errorCategory: 'other errors',
  })
}
